// components/SubscriptionGuard.tsx
// Wraps every protected page. When subscription is locked (expired/cancelled)
// it blurs the page content and shows the PaywallScreen overlay.

import { useState, useEffect } from "react";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import PaywallScreen from "./PaywallScreen";

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

export default function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { isLocked, loading } = useSubscription();
  const { profile } = useAuth();
  const width = useWindowWidth();
  // Sidebar is visible (and ~220px wide) only on desktop
  const sidebarWidth = width >= 768 ? 220 : 0;

  // Admins bypass subscription entirely
  if (profile?.role === "admin") return <>{children}</>;

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0d1117" }}>
        <div style={{ width: 28, height: 28, border: "3px solid rgba(6,182,212,0.2)", borderTopColor: "#06b6d4", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div style={{ position: "relative", minHeight: "100vh" }}>
        {/* Background content — blurred, non-interactive */}
        <div style={{ filter: "blur(3px)", pointerEvents: "none", userSelect: "none" }}>
          {children}
        </div>

        {/* Paywall overlay — respects sidebar width on desktop, full-screen on mobile */}
        <div style={{
          position: "fixed",
          top: 0,
          bottom: 0,
          left: sidebarWidth,
          right: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: width < 480 ? "16px 12px 32px" : "28px 20px 40px",
          background: "rgba(8,12,18,0.88)",
          backdropFilter: "blur(8px)",
          overflowY: "auto",
        }}>
          <PaywallScreen />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
